import React, {useContext, useEffect, useRef, useState} from "react";
import {FormContext} from "../../contexts/FormContext";
import {CartContext} from "../../contexts/CartContext";
import CircularProgress from "@material-ui/core/CircularProgress";
import {Alert} from "@mui/material";
import "./PayrocHostedFields.css";
import RecaptchaGate from "./RecaptchaGate";
import {isCheckoutVerified} from "../../utils/checkoutVerification";

const PAYROC_SDK_URLS = {
    test: "https://cdn.uat.payroc.com/js/hosted-fields/hosted-fields-1.7.0.261457.js",
    production: "https://cdn.payroc.com/js/hosted-fields/hosted-fields-1.7.0.261471.js"
};

const DEFAULT_CARD_BRANDS_IMAGE = "https://easy-ware-forms.s3.us-west-2.amazonaws.com/C_Clogos_124f1eaefb.jpg";
const PAYROC_FIELD_SYNC_DELAY_MS = 250;
const PAYROC_SESSION_REFRESH_BUFFER_MS = 60 * 1000;
const PAYROC_SESSION_MAX_AGE_MS = 9 * 60 * 1000;
const PAYROC_SESSION_REFRESH_MESSAGE = "Your secure payment session was refreshed. Please re-enter your card details before paying.";

const isInvalidPayrocSessionError = (error) =>
    /provided session token is invalid/i.test(error?.message || String(error || ''));

const PayrocHostedFields = ({handleFormSubmission, sendGoogleAnalytics}) => {
    const formContext = useContext(FormContext);
    const cartContext = useContext(CartContext);

    const venue = window.location.pathname.split("/")[1];
    const form = window.location.pathname.split("/")[2];

    const [loading, setLoading] = useState(true);
    const [readyToInit, setReadyToInit] = useState(false);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [billingZip, setBillingZip] = useState('');
    const [applePayAvailable, setApplePayAvailable] = useState(false);
    const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
    const [sessionCreatedAt, setSessionCreatedAt] = useState(null);
    const [sessionGeneration, setSessionGeneration] = useState(0);
    const [sessionRefreshRequested, setSessionRefreshRequested] = useState(0);
    const [sessionRefreshMessage, setSessionRefreshMessage] = useState(null);
    const hostedFieldsRef = useRef(null);
    const sessionRef = useRef(null);
    const terminalIdRef = useRef(null);
    const handleTokenReceivedRef = useRef(null);
    const sessionRefreshInProgressRef = useRef(false);
    const canSubmitRef = useRef(false);
    const paymentInFlightRef = useRef(false);
    const recaptchaEnabled = formContext.form?.enableRecaptcha === true;
    const needsVerification = recaptchaEnabled && !isCheckoutVerified(
        formContext.checkoutVerification, formContext.form, cartContext.custref);
    canSubmitRef.current = !needsVerification;

    const isTestMode = formContext.form?.testMode || false;

    const WORLDNET_TEST_TERMINAL_ID = '6025001';

    const getProcessingTerminalId = () => {
        if (isTestMode) {
            return WORLDNET_TEST_TERMINAL_ID;
        }
        if (cartContext.isRecurring && formContext.form?.recurringTerminalId?.terminal_id) {
            return formContext.form.recurringTerminalId.terminal_id;
        }
        if (formContext.form?.nonRecurringTerminalId?.terminal_id) {
            return formContext.form.nonRecurringTerminalId.terminal_id;
        }
        return formContext.form?.venue?.worldnet_terminal_id;
    };

    const loadPayrocSdk = () => {
        return new Promise((resolve, reject) => {
            if (window.Payroc) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = isTestMode ? PAYROC_SDK_URLS.test : PAYROC_SDK_URLS.production;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Payroc SDK'));
            document.head.appendChild(script);
        });
    };

    const requestSessionRefresh = () => {
        if (sessionRefreshInProgressRef.current) return;

        sessionRefreshInProgressRef.current = true;
        setSessionRefreshMessage(PAYROC_SESSION_REFRESH_MESSAGE);
        setSessionRefreshRequested((value) => value + 1);
    };

    const handleTokenReceived = async (token, processingTerminalId) => {
        if (!canSubmitRef.current) {
            setError('Please complete the reCAPTCHA checkbox before paying.');
            setProcessing(false);
            return;
        }
        if (paymentInFlightRef.current) return;
        paymentInFlightRef.current = true;
        try {
            console.log('Payroc Hosted Fields returned a single-use token.');
            setProcessing(true);
            setError(null);

            console.log('Calling handleFormSubmission...');
            await handleFormSubmission();
            console.log('handleFormSubmission complete');

            if (sendGoogleAnalytics) {
                try {
                    sendGoogleAnalytics();
                } catch (gaErr) {
                    console.warn('Google Analytics error (non-fatal):', gaErr);
                }
            }

            console.log('Sending token to /payroc/process-payment...', {
                custref: cartContext.custref,
                amount: cartContext.total,
                processingTerminalId
            });

            const response = await fetch(process.env.REACT_APP_SUBMIT_URL + "/payroc/process-payment", {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    token: token,
                    custref: cartContext.custref,
                    checkoutVerification: formContext.checkoutVerification?.token,
                    amount: cartContext.total,
                    currency: formContext.form?.venue?.worldnet_currency || 'USD',
                    processingTerminalId: processingTerminalId,
                    venue: venue,
                    form: form,
                    testMode: isTestMode,
                    billingZip: billingZip
                })
            });

            console.log('process-payment response status:', response.status);
            const result = await response.json();

            if (result.recaptchaRequired) {
                setError(result.error);
                setProcessing(false);
            } else if (result.success) {
                window.location.href = `/${venue}/${form}/success?paymentId=${result.paymentId}&approvalCode=${result.approvalCode}`;
            } else {
                window.location.href = `/${venue}/${form}/failure?error=${encodeURIComponent(result.error || 'Payment declined')}&responseCode=${encodeURIComponent(result.responseCode || '')}`;
            }
        } catch (err) {
            console.error('Payment processing error:', err);
            setError('An error occurred processing your payment. Please try again.');
            setProcessing(false);
        } finally {
            paymentInFlightRef.current = false;
            if (recaptchaEnabled) formContext.setCheckoutVerification(null);
        }
    };

    // Keep ref always pointing to the latest version of handleTokenReceived
    handleTokenReceivedRef.current = handleTokenReceived;

    const applePayConfigured = !!(
        formContext.form?.venue?.applePayEnabled &&
        formContext.form?.applePayEnabled
    );

    useEffect(() => {
        if (applePayConfigured && window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
            setApplePayAvailable(true);
        }
    }, [applePayConfigured]);

    const handleApplePay = async () => {
        if (!canSubmitRef.current) {
            setError('Please complete the reCAPTCHA checkbox before paying.');
            return;
        }
        const processingTerminalId = terminalIdRef.current;
        const appleDomainId = process.env.REACT_APP_APPLE_PAY_DOMAIN_ID;

        if (!appleDomainId) {
            setError('Apple Pay is not configured for this domain.');
            return;
        }

        const paymentRequest = {
            countryCode: formContext.form?.venue?.country || 'US',
            currencyCode: formContext.form?.venue?.worldnet_currency || 'USD',
            merchantCapabilities: ['supports3DS'],
            supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
            total: {
                label: formContext.form?.venue?.name || 'Payment',
                amount: cartContext.total.toFixed(2)
            }
        };

        const session = new window.ApplePaySession(3, paymentRequest);

        session.onvalidatemerchant = async (event) => {
            try {
                const startSessionResponse = await formContext.fetchApplePaySession(
                    processingTerminalId,
                    appleDomainId,
                    event.validationURL
                );
                session.completeMerchantValidation(JSON.parse(startSessionResponse));
            } catch (err) {
                console.error('Apple Pay merchant validation failed:', err);
                session.abort();
                setError('Apple Pay is unavailable. Please use the card form below.');
            }
        };

        session.onpaymentauthorized = async (event) => {
            try {
                setProcessing(true);
                setError(null);

                await handleFormSubmission();

                if (sendGoogleAnalytics) {
                    try { sendGoogleAnalytics(); } catch (e) { console.warn('GA error:', e); }
                }

                const response = await fetch(process.env.REACT_APP_SUBMIT_URL + "/payroc/apple-pay-payment", {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        applePayToken: event.payment.token,
                        custref: cartContext.custref,
                        checkoutVerification: formContext.checkoutVerification?.token,
                        amount: cartContext.total,
                        currency: formContext.form?.venue?.worldnet_currency || 'USD',
                        processingTerminalId,
                        venue,
                        form,
                        testMode: isTestMode
                    })
                });

                const result = await response.json();

                if (result.success) {
                    session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
                    window.location.href = `/${venue}/${form}/success?paymentId=${result.paymentId}&approvalCode=${result.approvalCode}`;
                } else {
                    session.completePayment(window.ApplePaySession.STATUS_FAILURE);
                    setError(result.error || 'Apple Pay payment declined. Please try the card form below.');
                    setProcessing(false);
                }
            } catch (err) {
                console.error('Apple Pay payment error:', err);
                session.completePayment(window.ApplePaySession.STATUS_FAILURE);
                setError('An error occurred. Please try the card form below.');
                setProcessing(false);
            } finally {
                if (recaptchaEnabled) formContext.setCheckoutVerification(null);
            }
        };

        session.oncancel = () => {
            setProcessing(false);
        };

        session.begin();
    };

    // Phase 1: Fetch a session token and load the SDK, then render the containers.
    // Payroc session tokens expire after 10 minutes. Refresh one minute early so the
    // hosted iframes are always initialized with a usable token.
    useEffect(() => {
        let cancelled = false;

        const prepare = async () => {
            try {
                sessionRefreshInProgressRef.current = true;
                setLoading(true);
                setError(null);
                setReadyToInit(false);
                setProcessing(false);

                const processingTerminalId = getProcessingTerminalId();
                if (!processingTerminalId) {
                    throw new Error('No processing terminal ID configured');
                }
                terminalIdRef.current = processingTerminalId;

                if (hostedFieldsRef.current?.destroy) {
                    try {
                        hostedFieldsRef.current.destroy();
                    } catch (destroyError) {
                        console.warn('Unable to close the previous Payroc Hosted Fields session.', destroyError);
                    }
                }
                hostedFieldsRef.current = null;

                const session = await formContext.fetchPayrocHostedFieldsSession(processingTerminalId);
                if (cancelled) return;
                if (!session) {
                    throw new Error('Failed to create hosted fields session');
                }
                sessionRef.current = session;
                setSessionExpiresAt(session.expiresAt || null);
                setSessionCreatedAt(Date.now());
                // Changing the key removes old Payroc iframes before initialization.
                setSessionGeneration((value) => value + 1);

                await loadPayrocSdk();
                if (cancelled) return;

                // Stop showing spinner — render the container divs
                setLoading(false);
                // Signal that we're ready to initialize (after DOM renders)
                setReadyToInit(true);
                sessionRefreshInProgressRef.current = false;
            } catch (err) {
                if (!cancelled) {
                    console.error('Payroc Hosted Fields preparation error:', err);
                    setError(err.message);
                    setLoading(false);
                    sessionRefreshInProgressRef.current = false;
                }
            }
        };

        if (formContext.form && cartContext.custref) {
            prepare();
        }

        return () => {
            cancelled = true;
        };
    }, [formContext.form, cartContext.custref, sessionRefreshRequested]);

    useEffect(() => {
        if (!sessionCreatedAt) return undefined;

        const expiresAtMs = Date.parse(sessionExpiresAt || '');
        const reportedRefreshAtMs = Number.isNaN(expiresAtMs)
            ? Number.POSITIVE_INFINITY
            : expiresAtMs - PAYROC_SESSION_REFRESH_BUFFER_MS;
        // Payroc documents a 10-minute Hosted Fields lifetime. Some UAT responses
        // have reported an expiresAt value far beyond that lifetime, so never trust
        // it beyond our documented nine-minute refresh deadline.
        const refreshAtMs = Math.min(
            reportedRefreshAtMs,
            sessionCreatedAt + PAYROC_SESSION_MAX_AGE_MS
        );
        const refreshDelayMs = Math.max(0, refreshAtMs - Date.now());
        const refreshTimer = window.setTimeout(requestSessionRefresh, refreshDelayMs);

        return () => window.clearTimeout(refreshTimer);
    }, [sessionCreatedAt, sessionExpiresAt]);

    useEffect(() => () => {
        if (hostedFieldsRef.current?.destroy) {
            hostedFieldsRef.current.destroy();
        }
    }, []);

    // Phase 2: Initialize hosted fields AFTER container divs are in the DOM
    useEffect(() => {
        if (!readyToInit || !sessionRef.current || !window.Payroc) return;

        const processingTerminalId = terminalIdRef.current;

        try {
            const primaryColor = formContext.form?.venue?.primaryColor || '#1976d2';
            const secondaryColor = formContext.form?.venue?.secondaryColor || '#333333';

            hostedFieldsRef.current = new window.Payroc.hostedFields({
                sessionToken: sessionRef.current.sessionToken,
                mode: "payment",
                // Payroc asks each iframe to persist its value before requesting
                // the single-use token. Yield briefly so those field requests are
                // registered before the SDK checks whether submission can proceed.
                onPreSubmit: () => new Promise((resolve) => {
                    if (!canSubmitRef.current) {
                        setError('Please complete the reCAPTCHA checkbox before paying.');
                        setProcessing(false);
                        resolve(false);
                        return;
                    }
                    window.setTimeout(() => resolve(true), PAYROC_FIELD_SYNC_DELAY_MS);
                }),
                fields: {
                    card: {
                        cardholderName: {
                            target: '#payroc-cardholder-name',
                            errorTarget: '#payroc-cardholder-name-error',
                            placeholder: 'Cardholder Name'
                        },
                        cardNumber: {
                            target: '#payroc-card-number',
                            errorTarget: '#payroc-card-number-error',
                            placeholder: '1234 5678 1234 1211'
                        },
                        expiryDate: {
                            target: '#payroc-expiry-date',
                            errorTarget: '#payroc-expiry-date-error',
                            placeholder: 'MM/YY'
                        },
                        cvv: {
                            wrapperTarget: '#payroc-cvv-wrapper',
                            target: '#payroc-cvv',
                            errorTarget: '#payroc-cvv-error',
                            placeholder: 'CVV'
                        },
                        submit: {
                            target: '#payroc-submit-button',
                            value: 'Pay Now'
                        }
                    }
                },
                styles: {
                    disableDefaultStyles: true,
                    css: {
                        'body': { 'margin': '0' },
                        'form': { 'display': 'flex', 'align-items': 'center' },
                        'input': {
                            'line-height': '2',
                            'box-sizing': 'border-box',
                            'border': '1px solid rgb(158, 158, 158)',
                            'width': '100%',
                            'height': '100%',
                            'padding': '8px',
                            'border-radius': '4px',
                            'background-color': '#FFF',
                            'color': 'rgb(99, 99, 99)',
                            'text-align': 'left',
                            'font-size': '16px',
                            'font-family': '"Roboto", "Helvetica", "Arial", sans-serif'
                        },
                        "input[type='text']": { 'min-height': '45px' },
                        'label': {
                            'padding': '8px 0',
                            'font-family': '"Roboto", "Helvetica", "Arial", sans-serif',
                            'font-size': '14px',
                            'display': 'inline-block',
                            'color': '#666'
                        },
                        ':focus': { 'outline': 'none', 'border-color': primaryColor },
                        '::placeholder': { 'color': 'rgb(158, 158, 158)' },
                        'input.invalid': {
                            'color': '#d32f2f',
                            'border-color': '#d32f2f'
                        },
                        'button': {
                            'background-color': secondaryColor,
                            'color': '#FFF',
                            'border': 'none',
                            'border-radius': '4px',
                            'width': '100%',
                            'text-align': 'center',
                            'min-height': '45px',
                            'padding': '10px 24px',
                            'font-size': '16px',
                            'font-family': '"Roboto", "Helvetica", "Arial", sans-serif',
                            'cursor': 'pointer'
                        },
                        'button:hover': { 'opacity': '0.9', 'cursor': 'pointer' },
                        '.loading-svg': { 'display': 'none', 'color': '#FFF' },
                        '.button-text': { 'display': 'inline-block' },
                        '.loading .loading-svg': { 'display': 'inline-block' },
                        '.loading .button-text': { 'display': 'none' },
                        '.spin': { 'animation': 'spin 1s linear infinite' },
                        '@keyframes spin': { 'to': { 'transform': 'rotate(1turn)' } }
                    }
                }
            });

            hostedFieldsRef.current.on('submissionSuccess', async (data) => {
                console.log('submissionSuccess event fired:', data);
                await handleTokenReceivedRef.current(data.token, processingTerminalId);
            });

            hostedFieldsRef.current.on('error', (err) => {
                console.error('Payroc Hosted Fields error:', err);
                if (isInvalidPayrocSessionError(err)) {
                    requestSessionRefresh();
                    return;
                }
                setError(err.message || 'Payment validation error');
                setProcessing(false);
            });

            hostedFieldsRef.current.on('submissionStart', () => {
                setProcessing(true);
                setError(null);
            });

            hostedFieldsRef.current.on('submissionError', (err) => {
                console.error('Payroc submission error:', err);
                if (isInvalidPayrocSessionError(err)) {
                    requestSessionRefresh();
                    return;
                }
                setError(err.message || 'Payment submission failed. Please try again.');
                setProcessing(false);
            });

            hostedFieldsRef.current.initialize();
        } catch (err) {
            console.error('Payroc Hosted Fields initialization error:', err);
            setError(err.message);
        }
    }, [readyToInit, sessionGeneration]);

    const venueData = formContext.form?.venue;
    const formData = formContext.form;
    const cardBrandsUrl = venueData?.creditCardBrandsImage?.url || DEFAULT_CARD_BRANDS_IMAGE;

    if (loading) {
        return (
            <div className="payroc-loading">
                <CircularProgress/>
                <p>Loading payment form...</p>
            </div>
        );
    }

    return (
        <>
        {(() => {
            const alignment = venueData?.hostedFieldsLogoAlignment || 'center';
            const layout = venueData?.hostedFieldsLogoLayout || 'above';
            const hasLogo = !!venueData?.logo?.url;
            const hasContact = !!(venueData?.address || venueData?.phone || venueData?.email);

            const headerClasses = ['payroc-venue-header'];
            if (layout === 'beside') headerClasses.push('payroc-layout-beside');
            if (alignment === 'center' && layout === 'above') headerClasses.push('payroc-logo-center');

            const contactBlock = hasContact && (
                <div id="payroc-venue-contact">
                    {venueData.address && <div>{venueData.address}</div>}
                    {(venueData.City || venueData.State || venueData.Zip) && (
                        <div>
                            {[venueData.City, venueData.State].filter(Boolean).join(', ')}
                            {venueData.Zip ? ` ${venueData.Zip}` : ''}
                        </div>
                    )}
                    {venueData.phone && <div>Phone: {venueData.phone}</div>}
                    {venueData.email && <div>Email: {venueData.email}</div>}
                </div>
            );

            if (layout === 'beside' && hasLogo && hasContact) {
                return (
                    <div id="payroc-venue-header" className={headerClasses.join(' ')}>
                        <img src={venueData.logo.url} alt={venueData.name || 'Venue logo'} />
                        {contactBlock}
                    </div>
                );
            }

            return (
                <>
                    {hasLogo && (
                        <div id="payroc-venue-header" className={headerClasses.join(' ')}>
                            <img src={venueData.logo.url} alt={venueData.name || 'Venue logo'} />
                        </div>
                    )}
                    {hasContact && (
                        <div className="payroc-venue-contact-wrapper">
                            {contactBlock}
                        </div>
                    )}
                </>
            );
        })()}
        <div key={sessionGeneration} id="payroc-hosted-fields" className="payroc-hosted-fields-container">
            {formData?.checkoutHTML && (
                <div
                    id="payroc-checkout-html"
                    dangerouslySetInnerHTML={{__html: formData.checkoutHTML}}
                />
            )}

            <div id="payroc-total">
                Total ${cartContext.total?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </div>

            {error && (
                <Alert id="payroc-error" severity="error">{error}</Alert>
            )}

            {sessionRefreshMessage && (
                <Alert id="payroc-session-refresh" severity="info">{sessionRefreshMessage}</Alert>
            )}

            {applePayAvailable && (
                <div id="payroc-apple-pay">
                    <button
                        className="apple-pay-button apple-pay-button-black"
                        onClick={handleApplePay}
                        disabled={processing || needsVerification}
                        aria-label="Pay with Apple Pay"
                    />
                    <div className="payroc-divider">
                        <span>or pay with card</span>
                    </div>
                </div>
            )}

            <div id="payroc-field-cardholder-name" className="payroc-field">
                <label id="payroc-label-cardholder-name" className="payroc-label">
                    Cardholder Name
                </label>
                <div id="payroc-cardholder-name" className="payroc-input-container"></div>
                <div id="payroc-cardholder-name-error" className="payroc-error-message"></div>
            </div>

            <div id="payroc-field-billing-zip" className="payroc-field">
                <label id="payroc-label-billing-zip" className="payroc-label">
                    Credit Card Billing Zip Code
                </label>
                <input
                    id="payroc-billing-zip-input"
                    type="text"
                    placeholder="12345"
                    value={billingZip}
                    onChange={(e) => setBillingZip(e.target.value)}
                    maxLength={10}
                />
            </div>

            <div id="payroc-field-card-number" className="payroc-field">
                <label id="payroc-label-card-number" className="payroc-label">
                    Card Number
                </label>
                <div id="payroc-card-number" className="payroc-input-container"></div>
                <div id="payroc-card-number-error" className="payroc-error-message"></div>
            </div>

            <div id="payroc-field-row-expiry-cvv" className="payroc-field-row">
                <div id="payroc-field-expiry-date" className="payroc-field payroc-field-half">
                    <label id="payroc-label-expiry-date" className="payroc-label">
                        Expiration Date
                    </label>
                    <div id="payroc-expiry-date" className="payroc-input-container"></div>
                    <div id="payroc-expiry-date-error" className="payroc-error-message"></div>
                </div>
                <div id="payroc-field-cvv" className="payroc-field payroc-field-half">
                    <label id="payroc-label-cvv" className="payroc-label">
                        CVV
                    </label>
                    <div id="payroc-cvv-wrapper">
                        <div id="payroc-cvv" className="payroc-input-container"></div>
                    </div>
                    <div id="payroc-cvv-error" className="payroc-error-message"></div>
                </div>
            </div>

            {recaptchaEnabled && (needsVerification
                ? <RecaptchaGate formId={formContext.form.id} custref={cartContext.custref}
                    onVerified={formContext.setCheckoutVerification}/>
                : <Alert severity="success">Verification complete. Ready for one payment attempt.</Alert>)}

            <div id="payroc-submit-button" className="payroc-submit-container"
                aria-disabled={needsVerification || processing}
                style={{pointerEvents: needsVerification || processing ? 'none' : 'auto', opacity: needsVerification ? 0.5 : 1}}></div>

            {processing && (
                <div id="payroc-processing" className="payroc-processing">
                    <CircularProgress size={24}/>
                    <p>Processing payment...</p>
                </div>
            )}

            <div id="payroc-card-brands">
                <img src={cardBrandsUrl} alt="Accepted credit cards" />
            </div>

            <div id="payroc-secure-footer">
                Your payment is securely processed through Payroc.
            </div>
        </div>
        </>
    );
};

export default PayrocHostedFields;
